import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Table from "../../components/Table";
import { usePersistStore, useStore } from "../../store";
import { AdService, PaymentService } from "../../services";
import { useBuyerPayments } from "../../hooks/queries";
import { Utils } from "../../utils";
import Confirm from "../../components/Confirm";
import AdDetailsPopup from "../../components/AdDetailsPopup";
import CommonModal from "../../components/CommonModal";
import { Dropdown } from "react-bootstrap";
import TrustapComplaintPopup from "../../components/TrustapComplaintPopup";
import PaymentStatusDropdown from "../../components/PaymentStatusDropdown";
import ShipmentStatusDropdown from "../../components/ShipmentStatusDropdown";
import { PAYMENT_STATUS, SHIPMENT_STATUS } from "../../utils/enums";
import {
  PaymentStatusChip,
  ShippingStatusChip,
} from "../../components/status";

function BuyerPayments() {
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [paymentStatus, setPaymentStatus] = useState<string>(
    PAYMENT_STATUS.ALL
  );
  const [shipmentStatus, setShipmentStatus] = useState<string>(
    SHIPMENT_STATUS.ALL
  );
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage, setSuccessMessage } = useStore(
    (s: any) => s
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);
  const [showGetVerifiedFirst, setShowGetVerifiedFirst] = useState(false);
  const [selectedObj, setSelectedObj] = useState<any>(null);
  const [showTrustapComplaintPopup, setShowTrustapComplaintPopup] =
    useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [busyRowIds, setBusyRowIds] = useState<Set<string>>(new Set());
  const markBusy = useCallback((id: string, busy: boolean) => {
    setBusyRowIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const renderPercentageChange = useCallback((row: any) => {
    return Utils.formatPercentChange(row?.adId?.price, row?.adId?.priceSold, 2);
  }, []);

  const renderBadge = useCallback(
    (row: any) => <PaymentStatusChip row={row} perspective="buyer" />,
    []
  );

  const handleTrackParcel = useCallback(
    async (row: any) => {
      if (!token || !row?._id) return;
      setIsLoading(true);
      let result = await PaymentService.trackShipment(token, {
        paymentId: row?._id,
      });

      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
      const trackUrl = result?.data?.url;
      if (!trackUrl) {
        setErrorMessage("Tracking link is not available yet.");
        return;
      }
      const w = window.open(trackUrl, "_blank");
      if (!w) {
        setErrorMessage(
          "Your browser blocked the tracking window. Please allow popups and try again."
        );
      }
    },
    [token, setErrorMessage, setIsLoading]
  );

  const handleConfirmDelivery = useCallback(
    async (row: any) => {
      if (!token || !row?._id) return;
      if (busyRowIds.has(row._id)) return;
      markBusy(row._id, true);
      setIsLoading(true);
      let result = await PaymentService.confirmDelivery(token, {
        paymentId: row?._id,
      });

      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        markBusy(row._id, false);
        return;
      }

      setIsLoading(false);
      setSuccessMessage("Delivery confirmed successfully.");
      markBusy(row._id, false);
      setReloadKey((k) => k + 1);
    },
    [
      token,
      setErrorMessage,
      setIsLoading,
      setSuccessMessage,
      busyRowIds,
      markBusy,
    ]
  );

  // Re-open the Trustap hosted-checkout / bank-instructions page in a new
  // tab. Used for bank-transfer rows still in `joined` so the buyer can
  // re-find their IBAN if they closed the original tab.
  const handleOpenInstructions = useCallback(
    (row: any) => {
      if (!row?.url) {
        setErrorMessage(
          "Payment instructions are not available for this transaction."
        );
        return;
      }
      const w = window.open(row.url, "_blank");
      if (!w) {
        setErrorMessage(
          "Your browser blocked the popup. Please allow popups for this site and try again."
        );
      }
    },
    [setErrorMessage]
  );

  const renderActions = useCallback(
    (row: any) => {
      const isBusy = busyRowIds.has(row?._id);
      const canTrack = !!row?.isShippingStarted && !row?.isShippingCompleted;
      const canConfirm =
        !!row?.isShippingCompleted && !row?.isDeliveryConfirmed;
      const canComplain = !!row?.isInComplaintWindow;
      // Bank-transfer rows that haven't settled yet — let the buyer reopen
      // the Trustap hosted page to view their IBAN/SWIFT instructions.
      const canViewInstructions =
        row?.paymentMethod === "bank_transfer" &&
        row?.status === "basic_tx.joined" &&
        !!row?.url;
      if (!canTrack && !canConfirm && !canComplain && !canViewInstructions)
        return null;
      return (
        <div className="profileDropdown">
          <Dropdown>
            <Dropdown.Toggle aria-label="Row actions">
              <i className="bi bi-three-dots-vertical" aria-hidden="true" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {canViewInstructions && (
                <Dropdown.Item onClick={() => handleOpenInstructions(row)}>
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-bank" aria-hidden="true" />
                    <span>View payment instructions</span>
                  </div>
                </Dropdown.Item>
              )}
              {canTrack && (
                <Dropdown.Item onClick={() => handleTrackParcel(row)}>
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-truck" aria-hidden="true" />
                    <span>Track your parcel</span>
                  </div>
                </Dropdown.Item>
              )}
              {canConfirm && (
                <Dropdown.Item
                  disabled={isBusy}
                  onClick={() => {
                    if (isBusy) return;
                    handleConfirmDelivery(row);
                  }}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-check2-circle" aria-hidden="true" />
                    <span>Confirm delivery</span>
                  </div>
                </Dropdown.Item>
              )}
              {canComplain && (
                <Dropdown.Item
                  onClick={() => {
                    setSelectedObj(row);
                    setShowTrustapComplaintPopup(true);
                  }}
                >
                  <div className="d-flex gap-2 align-items-center">
                    <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                    <span>Raise a complaint</span>
                  </div>
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      );
    },
    [
      handleTrackParcel,
      handleConfirmDelivery,
      handleOpenInstructions,
      setSelectedObj,
      setShowTrustapComplaintPopup,
      busyRowIds,
    ]
  );

  const renderMobileCard = useCallback(
    (row: any) => (
      <div className="bidsCard">
        <div className="bidsCard__top">
          <div>
            <Link
              to={`/details/${row?.adId?._id}`}
              className="bidsCard__title"
            >
              {row?.adId?.title}
            </Link>
            {row?.code && (
              <div className="refCode refCode--sub">{row.code}</div>
            )}
          </div>
          <div className="bidsCard__status">{renderBadge(row)}</div>
        </div>
        <div className="bidsCard__grid">
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Opening</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.adId?.price)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Paid</span>
            <span className="bidsCard__statValue">
              {Utils.formatToCurrency(row?.adId?.priceSold)}
            </span>
          </div>
          <div className="bidsCard__stat">
            <span className="bidsCard__statLabel">Change</span>
            <span className="bidsCard__statValue">
              {renderPercentageChange(row)}
            </span>
          </div>
        </div>
        <div className="bidsCard__meta">
          <div className="bidsCard__metaRow">
            <i className="bi bi-calendar-event" aria-hidden="true"></i>
            <span>
              {row?.status === "basic_tx.joined" ? "Started" : "Paid"}{" "}
              {Utils.formatDateTime(row?.statusUpdatedAt || row?.createdAt)}
            </span>
          </div>
          <div className="bidsCard__metaRow">
            <i className="bi bi-truck" aria-hidden="true"></i>
            <span>
              Shipping:{" "}
              <ShippingStatusChip
                isStarted={!!row?.isShippingStarted}
                isCompleted={!!row?.isShippingCompleted}
              />
              {row?.shipmentCode && (
                <span className="refCode refCode--sub ms-2">
                  {row.shipmentCode}
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="bidsCard__actions">{renderActions(row)}</div>
      </div>
    ),
    [renderBadge, renderPercentageChange, renderActions]
  );

  const columns = useMemo(
    () => [
      {
        title: "Code",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) =>
          row?.code ? (
            <span className="refCode">{row.code}</span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>—</span>
          ),
      },
      {
        title: "Title",
        thAlign: "left",
        tdAlign: "left",
        formatter: (row: any) => (
          <Link
            to={`/details/${row?.adId?._id}`}
            style={{ color: "var(--main-color)", textDecoration: "none" }}
          >
            {row?.adId?.title}
          </Link>
        ),
      },
      {
        title: "Opening Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.adId?.price),
      },
      {
        title: "Selling Price",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => Utils.formatToCurrency(row?.adId?.priceSold),
      },
      {
        title: "% Change",
        thAlign: "right",
        tdAlign: "right",
        formatter: (row: any) => renderPercentageChange(row),
      },
      {
        title: "Payment Date",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) =>
          Utils.formatDateTime(row?.statusUpdatedAt || row?.createdAt),
      },
      {
        title: "Status",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderBadge(row),
      },
      {
        title: "Shipping",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => (
          <div className="d-flex flex-column align-items-center gap-1">
            <ShippingStatusChip
              isStarted={!!row?.isShippingStarted}
              isCompleted={!!row?.isShippingCompleted}
            />
            {row?.shipmentCode && (
              <span className="refCode refCode--sub">
                {row.shipmentCode}
              </span>
            )}
          </div>
        ),
      },
      {
        title: "Actions",
        thAlign: "center",
        tdAlign: "center",
        formatter: (row: any) => renderActions(row),
      },
    ],
    [renderPercentageChange, renderBadge]
  );

  const { data, isLoading, error, refetch } = useBuyerPayments(token, {
    page,
    limit: rowsPerPage,
    paymentStatus,
    shipmentStatus,
  });
  const list = data?.list || [];
  const count = data?.count || 0;
  const isDone = !isLoading;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  // Action handlers bump `reloadKey` to request a refresh after a mutation.
  useEffect(() => {
    if (reloadKey > 0) refetch();
  }, [reloadKey, refetch]);

  const onPaymentStatusChange = useCallback((v: string) => {
    setPage(0);
    setPaymentStatus(v);
  }, []);
  const onShipmentStatusChange = useCallback((v: string) => {
    setPage(0);
    setShipmentStatus(v);
  }, []);

  const handleDeleteAd = useCallback(async () => {
    if (!selectedObj) return;
    setIsLoading(true);
    let result = await AdService.deleteById(token, selectedObj?._id);
    if (result.error) {
      setErrorMessage(result.error);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setSelectedObj(null);
    setShowDeleteConfirm(false);
    setReloadKey((k) => k + 1);
  }, [token, selectedObj, setErrorMessage, setIsLoading]);

  return (
    <>
      <Confirm
        show={showDeleteConfirm}
        onNo={() => {
          setSelectedObj(null);
          setShowDeleteConfirm(false);
        }}
        onYes={handleDeleteAd}
        title="Are you sure?"
        body="Are you sure you want to delete this artwork? This action is irreversible."
      />
      <AdDetailsPopup
        show={showDetailsPopup}
        onHide={() => {
          setSelectedObj(null);
          setShowDetailsPopup(false);
        }}
        data={selectedObj}
      />
      <CommonModal
        title="Get Verified"
        message="You need to get verified to perform this action."
        show={showGetVerifiedFirst}
        onHide={() => setShowGetVerifiedFirst(false)}
      />
      <TrustapComplaintPopup
        show={showTrustapComplaintPopup}
        onHide={() => {
          setSelectedObj(null);
          setShowTrustapComplaintPopup(false);
          setReloadKey((k) => k + 1);
        }}
        data={selectedObj}
      />
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4">
              <div className="tableCardHeader px-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">Payments</h2>
                  <p className="tableCardHeader__subtitle m-0">
                    {isDone
                      ? `${count} ${count === 1 ? "payment" : "payments"} total`
                      : "Loading your payments…"}
                  </p>
                </div>
                <div className="tableCardHeader__actions customTitle">
                  <PaymentStatusDropdown
                    value={paymentStatus}
                    onChange={onPaymentStatusChange}
                  />
                  <ShipmentStatusDropdown
                    value={shipmentStatus}
                    onChange={onShipmentStatusChange}
                  />
                </div>
              </div>
              <Table
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                page={page}
                setPage={setPage}
                columns={columns}
                rows={list}
                isDone={isDone}
                count={count}
                mobileCard={renderMobileCard}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default BuyerPayments;
